// Vertical analog of BrushSlider: selects a sub-range of a continuous
// numeric domain (typically the Y axis of a chart). Top of the track is
// the max, bottom is the min — matching how Y axes read on charts above.
//
// No labels by design — the chart's own Y-axis tick labels reflect the
// zoomed domain, so a second copy here would be noise.
//
// `padTop` / `padBottom` align the track with the chart's plot-area top
// and bottom (measured from Recharts' Y-axis line in the consumer).

import React, { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  min: number;
  max: number;
  startValue: number;
  endValue: number;
  onChange: (range: { startValue: number; endValue: number }) => void;
  width?: number;
  padTop?: number;
  padBottom?: number;
  trackColor?: string;
  rangeColor?: string;
  // Smallest selectable span, expressed as a fraction of (max-min).
  // Prevents the two handles from collapsing onto each other.
  minSpanFraction?: number;
}

const HANDLE_H = 8;
const HANDLE_W = 24;
const HIT_H = 22;
const HIT_W = 32;

type DragMode = 'start' | 'end' | 'range';

const VerticalBrushSlider: React.FC<Props> = ({
  min,
  max,
  startValue,
  endValue,
  onChange,
  width = 32,
  padTop,
  padBottom,
  trackColor = '#e5e7eb',
  rangeColor = '#8884d8',
  minSpanFraction = 0.02,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setHeight(el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const deferred = () => {
      update();
      requestAnimationFrame(update);
      setTimeout(update, 300);
    };
    window.addEventListener('resize', deferred);
    window.addEventListener('orientationchange', deferred);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', deferred);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', deferred);
      window.removeEventListener('orientationchange', deferred);
      vv?.removeEventListener('resize', deferred);
    };
  }, []);

  const trackTop = padTop ?? HANDLE_H;
  const trackBottom = Math.max(trackTop, height - (padBottom ?? HANDLE_H));
  const trackHeight = trackBottom - trackTop;
  const lineX = width / 2;

  const span = max - min;
  const minSpan = span * minSpanFraction;

  // Top of the track is max, bottom is min — invert the y mapping.
  const valueToY = (v: number): number => {
    if (span <= 0) return trackTop;
    const t = (v - min) / span;
    return trackBottom - t * trackHeight;
  };
  const yToValue = (y: number): number => {
    if (trackHeight <= 0) return min;
    const t = Math.max(0, Math.min(1, (trackBottom - y) / trackHeight));
    return min + t * span;
  };

  // Refs so the long-lived pointer handlers see the latest state.
  const startRef = useRef(startValue);
  const endRef = useRef(endValue);
  useEffect(() => { startRef.current = startValue; }, [startValue]);
  useEffect(() => { endRef.current = endValue; }, [endValue]);

  // Wheel-zoom the selection around the cursor's value position. Attached
  // natively so we can preventDefault — React's synthetic onWheel is
  // passive. stopPropagation keeps a chart-body wheel handler upstream
  // from firing as well.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      if (span <= 0 || trackHeight <= 0) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = el.getBoundingClientRect();
      const t = Math.max(0, Math.min(1, (trackBottom - (e.clientY - rect.top)) / trackHeight));
      const anchorValue = min + t * span;
      const curStart = startRef.current;
      const curEnd = endRef.current;
      const curSpan = curEnd - curStart;
      if (curSpan <= 0) return;
      const factor = e.deltaY < 0 ? 0.85 : 1 / 0.85;
      const newSpan = Math.max(minSpan, Math.min(span, curSpan * factor));
      // Fractional position of the anchor inside the current selection,
      // clamped so a cursor outside the selection just zooms toward the
      // nearest edge rather than flying away from it.
      const f = Math.max(0, Math.min(1, (anchorValue - curStart) / curSpan));
      let ns = anchorValue - f * newSpan;
      let ne = ns + newSpan;
      if (ns < min) { ne += min - ns; ns = min; }
      if (ne > max) { ns -= ne - max; ne = max; }
      if (ns < min) ns = min;
      if (ne > max) ne = max;
      if (ns !== curStart || ne !== curEnd) onChange({ startValue: ns, endValue: ne });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [min, max, span, minSpan, trackBottom, trackHeight, onChange]);

  const beginDrag = useCallback((mode: DragMode) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = wrapRef.current!.getBoundingClientRect();
    const downY = e.clientY;
    const initStart = startRef.current;
    const initEnd = endRef.current;

    const onMove = (ev: PointerEvent) => {
      if (mode === 'start') {
        const nv = Math.max(min, Math.min(endRef.current - minSpan, yToValue(ev.clientY - rect.top)));
        if (nv !== startRef.current) onChange({ startValue: nv, endValue: endRef.current });
      } else if (mode === 'end') {
        const nv = Math.min(max, Math.max(startRef.current + minSpan, yToValue(ev.clientY - rect.top)));
        if (nv !== endRef.current) onChange({ startValue: startRef.current, endValue: nv });
      } else {
        const dy = ev.clientY - downY;
        const shift = trackHeight > 0 ? -(dy / trackHeight) * span : 0;
        let ns = initStart + shift;
        let ne = initEnd + shift;
        if (ns < min) { ne += min - ns; ns = min; }
        if (ne > max) { ns -= ne - max; ne = max; }
        if (ns !== startRef.current || ne !== endRef.current) onChange({ startValue: ns, endValue: ne });
      }
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  }, [min, max, span, minSpan, trackHeight, onChange]);

  const sy = valueToY(startValue);
  const ey = valueToY(endValue);

  return (
    <div
      ref={wrapRef}
      style={{ width, height: '100%', position: 'relative', userSelect: 'none', touchAction: 'none' }}
    >
      {height > 0 && (
        <svg width={width} height={height} style={{ display: 'block' }}>
          <line
            x1={lineX} x2={lineX} y1={trackTop} y2={trackBottom}
            stroke={trackColor} strokeWidth={2} strokeLinecap="round"
          />
          {/* startValue sits at the bottom of the selected segment (small
              values low on the axis), endValue at the top. */}
          <line
            x1={lineX} x2={lineX} y1={ey} y2={sy}
            stroke={rangeColor} strokeWidth={2} strokeLinecap="round"
          />
          <rect
            x={lineX - HIT_W / 2}
            y={ey + HANDLE_H / 2}
            width={HIT_W}
            height={Math.max(0, sy - ey - HANDLE_H)}
            fill="transparent"
            style={{ cursor: 'pointer' }}
            onPointerDown={beginDrag('range')}
          />
          <g onPointerDown={beginDrag('start')} style={{ cursor: 'pointer' }}>
            <rect
              x={lineX - HIT_W / 2} y={sy - HIT_H / 2}
              width={HIT_W} height={HIT_H} fill="transparent"
            />
            <rect
              x={lineX - HANDLE_W / 2} y={sy - HANDLE_H / 2}
              width={HANDLE_W} height={HANDLE_H} rx={3} ry={3}
              fill="white" stroke={rangeColor} strokeWidth={1.5}
            />
          </g>
          <g onPointerDown={beginDrag('end')} style={{ cursor: 'pointer' }}>
            <rect
              x={lineX - HIT_W / 2} y={ey - HIT_H / 2}
              width={HIT_W} height={HIT_H} fill="transparent"
            />
            <rect
              x={lineX - HANDLE_W / 2} y={ey - HANDLE_H / 2}
              width={HANDLE_W} height={HANDLE_H} rx={3} ry={3}
              fill="white" stroke={rangeColor} strokeWidth={1.5}
            />
          </g>
        </svg>
      )}
    </div>
  );
};

export default VerticalBrushSlider;
